import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Chip, IconButton, Tooltip, Grid,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useExploradorStore } from '../store/exploradorStore';

const estadoColors: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  V: 'success',
  A: 'error',
  P: 'warning',
};

const estadoLabels: Record<string, string> = {
  V: 'Vigente',
  A: 'Anulado',
  P: 'Pendiente',
};

const sunatColors: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  ACE: 'success',
  REC: 'error',
  ENV: 'warning',
};

export const ExploradorVentasPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, totalRegistros, filtro, loading, fetchVentas, setFiltro, resetFiltro } = useExploradorStore();
  const [fechaDesde, setFechaDesde] = useState<Date | null>(null);
  const [fechaHasta, setFechaHasta] = useState<Date | null>(null);
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    fetchVentas();
  }, [fetchVentas]);

  const handleBuscar = () => {
    setFiltro({
      fechaDesde: fechaDesde ? format(fechaDesde, 'yyyy-MM-dd') : null,
      fechaHasta: fechaHasta ? format(fechaHasta, 'yyyy-MM-dd') : null,
      busqueda: busqueda || null,
      pagina: 1,
    });
    fetchVentas();
  };

  const handleLimpiar = () => {
    setFechaDesde(null);
    setFechaHasta(null);
    setBusqueda('');
    resetFiltro();
    fetchVentas();
  };

  const handlePageChange = (model: { page: number; pageSize: number }) => {
    setFiltro({ pagina: model.page + 1, tamanioPagina: model.pageSize });
    fetchVentas();
  };

  const columns: GridColDef[] = [
    { field: 'serie', headerName: 'Serie', width: 80 },
    { field: 'nroDocumento', headerName: 'Número', width: 100 },
    {
      field: 'fechaEmision',
      headerName: 'Fecha Emisión',
      width: 130,
      valueFormatter: (value: string) => {
        try { return format(new Date(value), 'dd/MM/yyyy', { locale: es }); } catch { return value; }
      },
    },
    { field: 'razonSocial', headerName: 'Cliente', flex: 1, minWidth: 200 },
    { field: 'tipoComprobante', headerName: 'Comprobante', width: 130 },
    {
      field: 'totalVenta',
      headerName: 'Total',
      width: 120,
      type: 'number',
      valueFormatter: (value: number) => `S/ ${(value ?? 0).toFixed(2)}`,
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={estadoLabels[params.value] || params.value || '-'}
          color={estadoColors[params.value] || 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'estadoSunat',
      headerName: 'SUNAT',
      width: 90,
      renderCell: (params) => params.value ? (
        <Chip label={params.value} color={sunatColors[params.value] || 'default'} size="small" variant="outlined" />
      ) : null,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Explorador de Ventas</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/ventas/nueva')}>
          Nueva Venta
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DatePicker
              label="Fecha Desde"
              value={fechaDesde}
              onChange={setFechaDesde}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DatePicker
              label="Fecha Hasta"
              value={fechaHasta}
              onChange={setFechaHasta}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Buscar (RUC, Número)"
              size="small"
              fullWidth
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleBuscar}>Buscar</Button>
              <Tooltip title="Limpiar filtros">
                <IconButton onClick={handleLimpiar}><ClearIcon /></IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ height: 600 }}>
        <DataGrid
          rows={items}
          columns={columns}
          getRowId={(row) => row.idFactura}
          loading={loading}
          paginationMode="server"
          rowCount={totalRegistros}
          paginationModel={{ page: filtro.pagina - 1, pageSize: filtro.tamanioPagina }}
          onPaginationModelChange={handlePageChange}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => navigate(`/ventas/${params.row.idFactura}`)}
          sx={{ cursor: 'pointer', border: 'none' }}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
};
